import Keyboard from "./Keyboard";
import React, { useState, useEffect } from "react";
import GuessRow from "./GuessRow";
import Leaderboard from "./Leaderboard";
import { useNavigate } from "react-router-dom";
import { DateTime } from "luxon";

const API_URL = process.env.REACT_APP_API_URL;
const ARIZONA = "America/Phoenix";
console.log("DEBUG - Using API URL:", API_URL);

function isValidHardModeGuess(guess, previousResults) {
  const guessLetters = guess.toUpperCase().split("");
  const mustBeAt = {};
  const mustInclude = new Set();
  const cannotBeAt = {};

  previousResults.forEach(({ guess, result }) => {
    guess.split("").forEach((letter, i) => {
      const symbol = result[i];
      if (symbol === "🟩") {
        mustBeAt[i] = letter;
      } else if (symbol === "🟨") {
        mustInclude.add(letter);
        if (!cannotBeAt[letter]) cannotBeAt[letter] = new Set();
        cannotBeAt[letter].add(i);
      } else if (symbol === "⬜") {
        if (!mustInclude.has(letter)) {
          cannotBeAt[letter] = null;
        }
      }
    });
  });

  for (let [i, letter] of Object.entries(mustBeAt)) {
    if (guessLetters[i] !== letter) {
      return `Letter "${letter}" must be in position ${parseInt(i) + 1}`;
    }
  }

  for (let letter of mustInclude) {
    if (!guessLetters.includes(letter)) {
      return `Guess must include letter "${letter}"`;
    }
    if (cannotBeAt[letter]) {
      for (let pos of cannotBeAt[letter]) {
        if (guessLetters[pos] === letter) {
          return `Letter "${letter}" cannot be in position ${pos + 1}`;
        }
      }
    }
  }

  for (let i = 0; i < guessLetters.length; i++) {
    const letter = guessLetters[i];
    const everGray = previousResults.some(
      (pr) => pr.result[pr.guess.indexOf(letter)] === "⬜"
    );
    if (everGray && !mustInclude.has(letter)) {
      return `Letter "${letter}" was already proven incorrect and cannot be used.`;
    }
  }

  return null;
}

const WordleGame = ({ playerName, gameMode }) => {
  const [hasPlayed, setHasPlayed] = useState(false);
  const [dailyMessage, setDailyMessage] = useState("");
  const [isNameReserved, setIsNameReserved] = useState(null);
  const [reservePassword, setReservePassword] = useState("");
  const [reserveSuccess, setReserveSuccess] = useState(false);
  const [reserveError, setReserveError] = useState("");
  const [showClaimPrompt, setShowClaimPrompt] = useState(false);
  const [skipReservationCheck, setSkipReservationCheck] = useState(false);

  const [startTime, setStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [timerRunning, setTimerRunning] = useState(false);
  const [letterStatuses, setLetterStatuses] = useState({});
  const [wordId, setWordId] = useState(null);
  const [guess, setGuess] = useState("");
  const [results, setResults] = useState([]);
  const [attemptsLeft, setAttemptsLeft] = useState(6);
  const [message, setMessage] = useState("");
  const [win, setWin] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const navigate = useNavigate();

  const markGamePlayed = () => {
    const today = DateTime.now().setZone(ARIZONA).toISODate();
    localStorage.setItem("lastPlayDate", today);
    setHasPlayed(true);
  };

  const updateLetterStatusesFromResults = (allResults) => {
    const updated = { ...letterStatuses };
    allResults.forEach(({ guess, result }) => {
      guess.split("").forEach((char, i) => {
        const status = result[i];
        const currentStatus = updated[char];
        if (status === "🟩") {
          updated[char] = "correct";
        } else if (status === "🟨" && currentStatus !== "correct") {
          updated[char] = "present";
        } else if (["⬜", "⬛"].includes(status) && !["correct", "present"].includes(currentStatus)) {
          updated[char] = "absent";
        }
      });
    });
    setLetterStatuses(updated);
  };

  const checkIfNameIsReserved = async () => {
    try {
      const res = await fetch(`${API_URL}/verify-name/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ player_name: playerName, password: "invalid-check" })
      });
      setIsNameReserved(res.status !== 404);
    } catch {
      setIsNameReserved(false);
    }
  };

  const handleReserveSubmit = async () => {
    setReserveError("");
    try {
      const response = await fetch(`${API_URL}/reserve-name/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ player_name: playerName, password: reservePassword })
      });
      const data = await response.json();
      if (response.ok) {
        setReserveSuccess(true);
        localStorage.setItem("nameReserved", "true");
        localStorage.setItem("reservedName", playerName);
      } else {
        setReserveError(data.error || "Something went wrong");
      }
    } catch {
      setReserveError("Network error. Try again later.");
    }
  };

  useEffect(() => {
    if (!hasPlayed) {
      fetch(`${API_URL}/daily-word/`)
        .then(res => res.json())
        .then(data => {
          if (data.word && data.word_id) setWordId(data.word_id);
          else setMessage("Error fetching daily word.");
        })
        .catch(() => setMessage("Error connecting to server."));
    }
  }, [hasPlayed]);

  useEffect(() => {
    fetch(`${API_URL}/leaderboard/`)
      .then(res => res.json())
      .then(data => setLeaderboard(data))
      .catch(err => console.error("Leaderboard fetch failed:", err));
  }, []);

  useEffect(() => {
    if (startTime && timerRunning && !win && gameMode !== "easy") {
      const interval = setInterval(() => {
        setElapsedTime((Date.now() - startTime) / 1000);
      }, 100);
      return () => clearInterval(interval);
    }
  }, [startTime, timerRunning, win, gameMode]);

  useEffect(() => {
    if ((win || attemptsLeft === 0) && playerName && !skipReservationCheck) {
      checkIfNameIsReserved();
    }
  }, [win, attemptsLeft, playerName, skipReservationCheck]);

  const submitGuess = async () => {
    if (guess.length !== 5) {
      setMessage("Guess must be exactly 5 letters.");
      return;
    }
    if (gameMode === "hard") {
      const validationError = isValidHardModeGuess(guess, results);
      if (validationError) {
        setMessage(`❌ ${validationError}`);
        updateLetterStatusesFromResults(results);
        return;
      }
    }

    const response = await fetch(`${API_URL}/submit-guess/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        player_name: playerName || "Player",
        word_id: wordId,
        guess: guess.toUpperCase(),
        time_taken: elapsedTime,
        mode: gameMode
      }),
    });

    if (!response.ok) {
      try {
        const errorData = await response.json();
        setMessage(errorData.error || "Invalid guess. Please try again.");
      } catch {
        setMessage("Invalid guess. Please try again.");
      }
      return;
    }

    localStorage.setItem("playerName", playerName);
    const data = await response.json();
    if (data.has_played_today && (data.win || data.attempts_left === 0)) {
      setHasPlayed(true);
    }

    if (data.error) {
      setMessage(data.error);
      updateLetterStatusesFromResults(results);
    } else {
      const newResults = [...results, { guess, result: data.result }];
      setResults(newResults);
      setAttemptsLeft(data.attempts_left);
      setWin(data.win);
      updateLetterStatusesFromResults(newResults);

      if (data.win) {
        setTimerRunning(false);
        setMessage(`🎉 Correct! You solved it in ${elapsedTime.toFixed(3)}s!`);
        markGamePlayed();
      } else if (data.attempts_left === 0) {
        setMessage("❌ No more attempts! Refresh to try again tomorrow.");
      } else {
        setMessage(`✅ Keep going! Attempts left: ${data.attempts_left}`);
      }
    }

    setGuess("");
  };

  if (showClaimPrompt) {
    return (
      <div style={{ textAlign: "center", padding: "20px" }}>
        <p>This name is reserved. Do you want to claim it?</p>
        <button onClick={() => setShowClaimPrompt(false) || setIsNameReserved(true)}>Yes, enter password</button>
        <button onClick={() => setShowClaimPrompt(false) || setSkipReservationCheck(true)}>No, play without reserving</button>
      </div>
    );
  }

  if (hasPlayed) {
    return (
      <div style={{ textAlign: "center", padding: "20px" }}>
        <h2>{win ? "🎉 You won!" : "❌ Game Over"}</h2>
        <p>You used {6 - attemptsLeft} guess{6 - attemptsLeft === 1 ? "" : "es"}.</p>
        {gameMode !== "easy" && (
          <p>Your time: {elapsedTime.toFixed(3)} second{elapsedTime === 1 ? "" : "s"}</p>
        )}
        <p>
          <a href="/leaderboard" style={{ textDecoration: "none", fontWeight: "bold" }}>
            🏆 View Full Leaderboard
          </a>
        </p>

        {isNameReserved === false && !reserveSuccess && (
          <div style={{ marginTop: "30px" }}>
            <h3>🔐 Reserve your name</h3>
            <p>Want to save your name and scores for next time?</p>
            <input
              type="password"
              value={reservePassword}
              onChange={(e) => setReservePassword(e.target.value)}
              placeholder="Choose a password"
              style={{ padding: "8px", fontSize: "14px", width: "200px" }}
            />
            <br />
            <button onClick={handleReserveSubmit} style={{ marginTop: "10px" }}>
              Reserve Name
            </button>
            {reserveError && <p style={{ color: "red" }}>{reserveError}</p>}
          </div>
        )}
        {reserveSuccess && (
          <p style={{ marginTop: "20px", color: "green" }}>
            ✅ Name reserved successfully!
          </p>
        )}
      </div>
    );
  }

  return (
    <div style={{ textAlign: "center", padding: "20px" }}>
      <div style={{ minHeight: "28px", marginBottom: "8px" }}>
        <p style={{ margin: 0, visibility: message ? "visible" : "hidden" }}>{message}</p>
      </div>
      <div style={{ minHeight: "24px", marginBottom: "16px" }}>
        <h3 style={{ margin: 0 }}>Time Elapsed: {elapsedTime.toFixed(3)}s</h3>
      </div>
      {Array.from({ length: 6 }).map((_, i) => {
        const guessEntry = results[i];
        const isCurrent = i === results.length;
        return (
          <GuessRow
            key={i}
            guess={guessEntry ? guessEntry.guess : isCurrent ? guess : ""}
            result={guessEntry ? guessEntry.result : ""}
          />
        );
      })}
      <div>
        {win ? (
          <p>🎉 You won! Refresh to play again.</p>
        ) : attemptsLeft > 0 ? (
          <Keyboard
            onKeyPress={(key) => {
              if (!timerRunning) {
                setStartTime(Date.now());
                setTimerRunning(true);
              }
              if (guess.length < 5) {
                setGuess((prev) => prev + key);
              }
            }}
            onEnter={submitGuess}
            onBackspace={() => setGuess((prev) => prev.slice(0, -1))}
            letterStatuses={letterStatuses}
          />
        ) : (
          <p>❌ No more attempts! Refresh to try again tomorrow.</p>
        )}
      </div>

      {(win || attemptsLeft === 0) && isNameReserved === false && (
        <div style={{ marginTop: "30px" }}>
          <h3>🔐 Reserve your name</h3>
          <p>Want to save your name and scores for next time?</p>
          <input
            type="password"
            value={reservePassword}
            onChange={(e) => setReservePassword(e.target.value)}
            placeholder="Choose a password"
            style={{ padding: "8px", fontSize: "14px", width: "200px" }}
          />
          <br />
          <button onClick={handleReserveSubmit} style={{ marginTop: "10px" }}>
            Reserve Name
          </button>
          {reserveError && <p style={{ color: "red" }}>{reserveError}</p>}
        </div>
      )}
      {reserveSuccess && (
        <p style={{ marginTop: "20px", color: "green" }}>
          ✅ Name reserved successfully!
        </p>
      )}

      <p style={{ marginTop: "20px" }}>
        <button
          onClick={() => navigate("/leaderboard", { state: { hasPlayedToday: true } })}
          style={{
            background: "none",
            border: "none",
            color: "blue",
            cursor: "pointer",
            fontWeight: "bold",
            textDecoration: "underline",
          }}
        >
          🏆 View Full Leaderboard
        </button>
      </p>
    </div>
  );
};

export default WordleGame;
